/**
 * ArbitrationParticipants Component
 *
 * Compact participants panel for the arbitration sidebar.
 * Shows each party's role, online status, and quick contact actions.
 * Theme-aware (alpha tints) for dark + light.
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  IconButton,
  Badge,
  useTheme,
  alpha
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GroupIcon from '@mui/icons-material/Group';
import { IParticipant } from '../../services/arbitrationService';

interface ArbitrationParticipantsProps {
  participants: IParticipant[];
  onlineUsers?: Set<string>;
  typingUsers?: Map<string, string>;
  onViewProfile?: (userId: string) => void;
}

// Role configurations — hues match the chat bubbles for a coherent identity
const RoleConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  buyer: { icon: <PersonIcon />, color: '#0ea5e9', label: 'خریدار' },
  seller: { icon: <StoreIcon />, color: '#f59e0b', label: 'فروشنده' },
  admin: { icon: <AdminPanelSettingsIcon />, color: '#8b5cf6', label: 'داور' },
  party_a: { icon: <SwapHorizIcon />, color: '#06b6d4', label: 'طرف اول' },
  party_b: { icon: <SwapHorizIcon />, color: '#10b981', label: 'طرف دوم' }
};

const ParticipantItem: React.FC<{
  participant: IParticipant;
  isOnline: boolean;
  isTyping: boolean;
  isDark: boolean;
  onViewProfile?: (userId: string) => void;
}> = ({ participant, isOnline, isTyping, isDark, onViewProfile }) => {
  const config = RoleConfig[participant.role] || RoleConfig.buyer;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: 2,
        bgcolor: alpha(config.color, isDark ? 0.08 : 0.04),
        border: `1px solid ${alpha(config.color, isDark ? 0.28 : 0.18)}`,
        transition: 'background-color .15s',
        '&:hover': { bgcolor: alpha(config.color, isDark ? 0.14 : 0.08) }
      }}
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        badgeContent={
          <FiberManualRecordIcon
            sx={{
              fontSize: 11,
              color: isOnline ? 'success.main' : 'text.disabled',
              bgcolor: 'background.paper',
              borderRadius: '50%'
            }}
          />
        }
      >
        <Avatar
          src={participant.avatar}
          sx={{
            bgcolor: alpha(config.color, isDark ? 0.35 : 0.16),
            color: config.color,
            border: `2px solid ${alpha(config.color, 0.55)}`,
            width: 38,
            height: 38
          }}
        >
          {config.icon}
        </Avatar>
      </Badge>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {participant.name}
          </Typography>
          <Chip
            label={config.label}
            size="small"
            sx={{
              height: 17,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: alpha(config.color, isDark ? 0.25 : 0.12),
              color: config.color,
              flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        </Box>
        {isTyping ? (
          <Typography
            variant="caption"
            sx={{
              color: 'success.main',
              fontWeight: 600,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } }
            }}
          >
            در حال تایپ…
          </Typography>
        ) : (
          participant.email && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', direction: 'ltr', textAlign: 'right' }}>
              {participant.email}
            </Typography>
          )
        )}
      </Box>

      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        {participant.email && (
          <Tooltip title="ارسال ایمیل">
            <IconButton size="small" onClick={() => (window.location.href = `mailto:${participant.email}`)}>
              <EmailIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {onViewProfile && (
          <Tooltip title="مشاهده پروفایل">
            <IconButton size="small" onClick={() => onViewProfile(participant.userId)}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ArbitrationParticipants: React.FC<ArbitrationParticipantsProps> = ({
  participants,
  onlineUsers = new Set(),
  typingUsers = new Map(),
  onViewProfile
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const sortedParticipants = [...participants].sort((a, b) => {
    const roleOrder: Record<string, number> = { admin: 0, buyer: 1, party_a: 1, seller: 2, party_b: 2 };
    return (roleOrder[a.role] || 99) - (roleOrder[b.role] || 99);
  });

  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <GroupIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={800}>
            شرکت‌کنندگان
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FiberManualRecordIcon sx={{ fontSize: 9, color: onlineUsers.size > 0 ? 'success.main' : 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            {onlineUsers.size} آنلاین · {participants.length} نفر
          </Typography>
        </Box>
      </Box>

      {/* List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {sortedParticipants.map((participant) => (
          <ParticipantItem
            key={participant.userId}
            participant={participant}
            isOnline={onlineUsers.has(participant.userId)}
            isTyping={typingUsers.has(participant.userId)}
            isDark={isDark}
            onViewProfile={onViewProfile}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default ArbitrationParticipants;
